/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode, useCallback } from 'react';
import { Trans } from 'react-i18next';

import { Modal, Button, Row } from 'components/Blocks';
import { CheckIcon, WarningIcon, XIcon } from 'components/Icons';

import { useLang, useToggle } from 'hooks';
import { cn } from 'styles';

export interface ConfirmationModalProps {
  onConfirm: () => any | Promise<any>;
  cancel: () => any;
  text?: ReactNode;
  title?: string;
  type?: 'normal' | 'irreversible';
  size?: 'small' | 'medium' | 'big';
  isOpen: boolean;
}

const widthBySize = {
  small: 'w-[min(40vw,500px)]',
  medium: 'w-[min(60vw,750px)]',
  big: 'w-[min(90vw,1200px)]',
};

export function ConfirmationModal({
  cancel,
  onConfirm,
  text,
  type,
  size = 'small',
  title,
  isOpen,
}: ConfirmationModalProps): JSX.Element {
  const { isOn, toggle } = useToggle();

  const { t } = useLang();

  const executeConfirmation = useCallback(async () => {
    toggle();

    await onConfirm();

    toggle();
  }, [onConfirm, toggle]);

  const modalTitle = title || t('are-you-sure');

  return (
    <Modal
      isOpen={isOpen}
      close={cancel}
      title={modalTitle}
      gap="7"
      className={cn(widthBySize[size], 'phone:w-[90vw]')}
    >
      {type === 'irreversible' && (
        <Row gap="2" justifyContent="center">
          <WarningIcon color="primary" size={32} />
          <p className="text-center">
            <Trans>this-action-is-irreversible</Trans>
          </p>
        </Row>
      )}

      {!!text && (
        <Row justifyContent="center">{typeof text === 'string' ? <p>{text}</p> : text}</Row>
      )}

      <Row gap="6" justifyContent="space-between">
        <Button variant="outlined" onClick={cancel}>
          <XIcon />
          {t('cancel')}
        </Button>

        <Button disabled={isOn} onClick={executeConfirmation} loading={isOn}>
          <CheckIcon />
          {t('confirm')}
        </Button>
      </Row>
    </Modal>
  );
}
